/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  CatalogIcon,
  DocsIcon,
  Link,
  useContent,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { searchPlugin } from '@backstage/plugin-search';
import {
  DefaultResultListItem,
  SearchFilter,
  SearchBar,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { Box, Tab, TabList, TabPanel, Tabs } from '@backstage/ui';
import { RiExternalLinkLine } from '@remixicon/react';
import styles from './SearchModal.module.css';

const FOCUS_DELAY_MS = 195;

const searchTypes = [
  { key: 'all', name: 'All' },
  { key: 'software-catalog', name: 'Software Catalog' },
  { key: 'techdocs', name: 'Documentation' },
];

export const SearchModal = ({ toggleModal }: { toggleModal: () => void }) => {
  const getSearchLink = useRouteRef(searchPlugin.routes.root);

  const catalogApi = useApi(catalogApiRef);
  const { term, types, setTypes, setPageCursor } = useSearch();
  const { focusContent } = useContent();

  const selectedType = types.length === 0 ? 'all' : types[0];

  const handleTypeChange = (key: React.Key) => {
    setTypes(key === 'all' ? [] : [String(key)]);
    setPageCursor(undefined);
  };

  const handleResultClick = () => {
    toggleModal();
    setTimeout(focusContent, FOCUS_DELAY_MS);
  };

  const handleResultKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleResultClick();
    }
  };

  return (
    <Box className={styles.modal}>
      <Box className={styles.header}>
        <SearchBar className={styles.searchBar} margin="none" />
        <Box className={styles.tabs}>
          <Tabs selectedKey={selectedType} onSelectionChange={handleTypeChange}>
            <TabList>
              {searchTypes.map(type => (
                <Tab key={type.key} id={type.key}>
                  {type.name}
                </Tab>
              ))}
            </TabList>
            {searchTypes.map(type => (
              <TabPanel key={type.key} id={type.key} />
            ))}
          </Tabs>
        </Box>
      </Box>
      <Box className={styles.toolbar}>
        {types.includes('techdocs') && (
          <SearchFilter.Select
            className={styles.filter}
            label="Entity"
            name="name"
            values={async () => {
              // Return a list of entities which are documented.
              const { items } = await catalogApi.getEntities({
                fields: ['metadata.name'],
                filter: {
                  'metadata.annotations.backstage.io/techdocs-ref':
                    CATALOG_FILTER_EXISTS,
                },
              });

              const names = items.map(entity => entity.metadata.name);
              names.sort();
              return names;
            }}
          />
        )}
        <SearchFilter.Select
          className={styles.filter}
          label="Kind"
          name="kind"
          values={['Component', 'Template']}
        />
        <SearchFilter.Select
          className={styles.filter}
          label="Lifecycle"
          name="lifecycle"
          values={['experimental', 'production']}
        />
        <Link
          className={styles.fullResults}
          onClick={handleResultClick}
          to={`${getSearchLink()}?query=${term}`}
        >
          <span>View Full Results</span>
          <RiExternalLinkLine size={16} />
        </Link>
      </Box>
      <Box className={styles.results}>
        <SearchResult>
          {({ results }) => (
            <div className={styles.resultList}>
              {results.map(({ type, document, highlight, rank }) => {
                let resultItem;
                switch (type) {
                  case 'software-catalog':
                    resultItem = (
                      <CatalogSearchResultListItem
                        icon={<CatalogIcon />}
                        key={document.location}
                        result={document}
                        highlight={highlight}
                        rank={rank}
                      />
                    );
                    break;
                  case 'techdocs':
                    resultItem = (
                      <TechDocsSearchResultListItem
                        icon={<DocsIcon />}
                        key={document.location}
                        result={document}
                        highlight={highlight}
                        rank={rank}
                      />
                    );
                    break;
                  default:
                    resultItem = (
                      <DefaultResultListItem
                        key={document.location}
                        result={document}
                        highlight={highlight}
                        rank={rank}
                      />
                    );
                }
                return (
                  <div
                    className={styles.resultButton}
                    role="button"
                    tabIndex={0}
                    key={`${document.location}-btn`}
                    onClick={handleResultClick}
                    onKeyDown={handleResultKeyDown}
                  >
                    {resultItem}
                  </div>
                );
              })}
            </div>
          )}
        </SearchResult>
      </Box>
      <Box className={styles.footer}>
        <SearchResultPager />
      </Box>
    </Box>
  );
};
